/**
 * get_flow tool - Get full flow definition
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const getFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID) to retrieve"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  includeDefinition: z
    .boolean()
    .optional()
    .default(true)
    .describe("Include the full flow definition JSON (default true)"),
  format: z
    .enum(["summary", "json", "both"])
    .optional()
    .default("summary")
    .describe(
      "Output format: 'summary' = human-readable top-level overview (default), " +
        "'json' = full raw flow.properties.definition with all nested actions, " +
        "'both' = summary followed by full JSON. Use 'json' or 'both' to capture nested actions inside Switch/If/Foreach/Scope.",
    ),
});

export type GetFlowInput = z.infer<typeof getFlowInputSchema>;

export const getFlowTool = {
  name: "get_flow",
  description:
    "Get the complete definition of a Power Automate flow including triggers, actions, connection references, and description. Use list_flows first to find flow IDs. Set format='json' or format='both' to capture the FULL definition including nested actions inside Switch/If/Foreach/Scope (the default 'summary' format only shows top-level actions).",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID) to retrieve",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      includeDefinition: {
        type: "boolean",
        description: "Include the full flow definition JSON (default true)",
        default: true,
      },
      format: {
        type: "string",
        enum: ["summary", "json", "both"],
        description:
          "Output format: 'summary' = top-level overview (default), 'json' = full raw definition with all nested actions, 'both' = summary plus full JSON.",
        default: "summary",
      },
    },
    required: ["flowId"],
  },
};

export interface GetFlowResult {
  id: string;
  displayName: string;
  description?: string;
  state: string;
  createdTime: string;
  lastModifiedTime: string;
  triggerType: string;
  actionCount: number;
  connectionReferences: Array<{
    name: string;
    connectorId: string;
    connectionName: string;
    source?: string;
    tier: string;
  }>;
  definition?: unknown;
}

export async function handleGetFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = getFlowInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId, format: parsed.format }, "Executing get_flow");

  const flow = await api.getFlow(parsed.flowId, parsed.environment);

  const triggers = flow.properties.definition.triggers;
  const actions = flow.properties.definition.actions;

  const triggerNames = Object.keys(triggers);
  const firstTrigger = triggerNames[0] ? triggers[triggerNames[0]] : undefined;

  // `name` is the reference KEY (e.g. "shared_jira") and `id` is the connector
  // TYPE — neither says which connection the flow is actually bound to. That is
  // `connectionName`, and dropping it made a trigger outage undiagnosable: the
  // flow named its connector but not the (broken) connection carrying it, so the
  // binding had to be inferred from the portal. It is also exactly the field
  // `update_flow` takes back, so emitting it makes read → rebind a round trip.
  const connectionRefs = Object.entries(flow.properties.connectionReferences).map(
    ([name, ref]) => ({
      name,
      connectorId: ref.id,
      connectionName: ref.connectionName,
      ...(ref.source ? { source: ref.source } : {}),
      tier: ref.tier,
    })
  );

  // Descriptions live inside the workflow definition (the flows API rejects
  // properties.description on write); older portal flows may still surface it
  // at properties level — read both.
  const description = flow.properties.description ?? flow.properties.definition?.description;
  const creator = flow.properties.creator;

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    id: flow.name,
    displayName: flow.properties.displayName,
    state: flow.properties.state,
    createdTime: flow.properties.createdTime,
    lastModifiedTime: flow.properties.lastModifiedTime,
    ...(description ? { description } : {}),
    trigger: firstTrigger
      ? {
          name: triggerNames[0],
          type: firstTrigger.type,
          ...(firstTrigger.kind ? { kind: firstTrigger.kind } : {}),
        }
      : null,
    actionCount: Object.keys(actions).length,
    ...(creator
      ? {
          owner: {
            id: creator.id,
            displayName: creator.displayName,
            email: creator.email,
          },
        }
      : {}),
    connectionReferences: connectionRefs,
  };

  // 'json' mode: return only the raw definition for round-trip safety.
  if (parsed.format === "json") {
    const payload = {
      id: flow.name,
      displayName: flow.properties.displayName,
      description,
      state: flow.properties.state,
      createdTime: flow.properties.createdTime,
      lastModifiedTime: flow.properties.lastModifiedTime,
      creator,
      connectionReferences: flow.properties.connectionReferences,
      definition: flow.properties.definition,
    };
    return {
      text: JSON.stringify(payload, null, 2),
      structured: { ...structured, definition: flow.properties.definition },
    };
  }

  // 'summary' or 'both': build the human-readable summary.
  const lines = [
    `## ${flow.properties.displayName}`,
    ``,
    `**ID:** ${flow.name}`,
    `**Status:** ${flow.properties.state}`,
    `**Trigger:** ${firstTrigger?.type ?? "unknown"}${firstTrigger?.kind ? ` (${firstTrigger.kind})` : ""}`,
    `**Actions:** ${Object.keys(actions).length}`,
    `**Modified:** ${new Date(flow.properties.lastModifiedTime).toLocaleDateString()}`,
  ];

  if (description) {
    lines.push(`**Description:** ${description}`);
  }
  if (creator?.displayName || creator?.email) {
    lines.push(`**Owner:** ${creator.displayName ?? creator.email}${creator.id ? ` (${creator.id})` : ""}`);
  }

  if (connectionRefs.length > 0) {
    lines.push(``, `### Connections`);
    for (const conn of connectionRefs) {
      const tier = conn.tier === "Premium" ? " [Premium]" : "";
      // The bound connection id belongs in the prose too — a reader diagnosing a
      // connector failure needs to know WHICH connection, not just which connector.
      const bound = conn.connectionName ? ` → connection ${conn.connectionName}` : "";
      lines.push(`• ${conn.name}${tier}${bound}`);
    }
  }

  if (parsed.includeDefinition !== false) {
    lines.push(``, `### Actions (top-level)`);
    for (const [name, action] of Object.entries(actions)) {
      const act = action as { type: string; runAfter?: Record<string, unknown>; inputs?: unknown };
      const deps = act.runAfter ? Object.keys(act.runAfter).join(", ") : "trigger";
      lines.push(`• **${name}** (${act.type}) → runs after: ${deps}`);
      if (act.inputs) {
        lines.push(`  inputs: ${JSON.stringify(act.inputs, null, 2).split('\n').join('\n  ')}`);
      }
    }
    lines.push(``);
    lines.push(`_Note: Nested actions inside Switch/If/Foreach/Scope are not expanded in this view. Call get_flow with format="json" or format="both" to capture them._`);

    // Structured mirror of the top-level action list shown above.
    structured.actions = Object.entries(actions).map(([name, action]) => {
      const act = action as { type: string; runAfter?: Record<string, unknown>; inputs?: unknown };
      return {
        name,
        type: act.type,
        runsAfter: act.runAfter ? Object.keys(act.runAfter) : [],
        ...(act.inputs !== undefined ? { inputs: act.inputs } : {}),
      };
    });
  }

  if (parsed.format === "both") {
    lines.push(``, `### Full Definition (JSON)`, "```json", JSON.stringify(flow.properties.definition, null, 2), "```");
    structured.definition = flow.properties.definition;
  }

  return { text: lines.join("\n"), structured };
}
